<template>
  <div class="security-dashboard">

    <!-- ✅ WELCOME POPUP -->
    <transition name="fade">
      <div class="welcome-overlay" v-if="showWelcomePopup" @click.self="closeWelcomePopup">
        <div class="welcome-popup" :class="{ 'has-alerts': isFullyWarn }">
          <div class="popup-icon" :class="{ 'alert': isFullyWarn }">
            <span class="material-symbols-outlined">
              {{ isFullyWarn ? 'warning' : 'verified' }}
            </span>
          </div>

          <div class="popup-content">
            <h2>{{ isFullyWarn ? 'Attention requise' : 'Tout va bien' }}</h2>
            <p v-if="isFullyWarn">
              {{ alertCount }} capteur{{ alertCount > 1 ? 's' : '' }}
              {{ alertCount > 1 ? 'sont' : 'est' }} en dépassement de seuil
            </p>
            <p v-else>
              Tous les systèmes fonctionnent normalement
            </p>
          </div>

          <button class="btn-confirm" @click="closeWelcomePopup">
            <span>J'ai compris</span>
            <span class="material-symbols-outlined">check</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Header -->
    <header class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon" :class="mode.toLowerCase()">
            <span class="material-symbols-outlined">{{ mode === 'CO2' ? 'air' : 'electric_bolt' }}</span>
          </div>
          <div class="header-info">
            <h1 class="header-title">{{ mode === 'CO2' ? 'Sécurité CO2' : 'Suivi Consommation Électrique' }}</h1>
            <p class="header-subtitle">
              <span class="status-dot" :class="globalStatus"></span>
              {{ statusText }}
            </p>
          </div>
        </div>

        <div class="header-actions">
          <!-- Mode Toggle -->
          <div class="mode-toggle">
            <button
                type="button"
                class="mode-btn"
                :class="{ active: mode === 'CO2' }"
                @click="switchMode('CO2')"
            >
              <span class="material-symbols-outlined">air</span>
              <span class="mode-label">CO2</span>
            </button>
            <button
                type="button"
                class="mode-btn elec"
                :class="{ active: mode === 'ELEC' }"
                @click="switchMode('ELEC')"
            >
              <span class="material-symbols-outlined">electric_bolt</span>
              <span class="mode-label">Électrique</span>
            </button>
          </div>

          <!-- Refresh Button -->
          <button class="btn-refresh" @click="refresh" :disabled="true" :title="loading ? 'Chargement...' : 'Actualiser'">
            <span class="material-symbols-outlined" :class="{ spinning: loading }">sync</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Alert Bar -->
    <transition name="slide-down">
      <div class="alert-bar" v-if="isFullyWarn && !alertDismissed">
        <div class="alert-content">
          <span class="material-symbols-outlined alert-icon">warning</span>
          <div class="alert-text">
            <strong>Attention</strong>
            <span>{{ alertCount }} capteur{{ alertCount > 1 ? 's' : '' }} en dépassement de seuil</span>
          </div>
        </div>
        <button class="alert-dismiss" @click="alertDismissed = true">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </transition>

    <!-- Main Content - Scrollable -->
    <main class="dashboard-main">
      <!-- Loading Overlay -->
      <div class="loading-overlay" v-if="loading && !securityReady">
        <div class="loading-card">
          <div class="loading-spinner"></div>
          <h3>Chargement des données</h3>
          <p>Récupération des mesures en cours...</p>
        </div>
      </div>

      <!-- Content -->
      <div class="dashboard-content" v-if="securityReady">
        <!-- Stats Overview -->
<!--        <div class="stats-bar">-->
<!--          <div class="stat-item">-->
<!--            <span class="material-symbols-outlined">business</span>-->
<!--            <div class="stat-info">-->
<!--              <span class="stat-value">{{ profile.clients.length }}</span>-->
<!--              <span class="stat-label">Client{{ profile.clients.length > 1 ? 's' : '' }}</span>-->
<!--            </div>-->
<!--          </div>-->
<!--          <div class="stat-item">-->
<!--            <span class="material-symbols-outlined">domain</span>-->
<!--            <div class="stat-info">-->
<!--              <span class="stat-value">{{ totalSites }}</span>-->
<!--              <span class="stat-label">Univers</span>-->
<!--            </div>-->
<!--          </div>-->
<!--          <div class="stat-item">-->
<!--            <span class="material-symbols-outlined">sensors</span>-->
<!--            <div class="stat-info">-->
<!--              <span class="stat-value">{{ totalSensors }}</span>-->
<!--              <span class="stat-label">Capteur{{ totalSensors > 1 ? 's' : '' }}</span>-->
<!--            </div>-->
<!--          </div>-->
<!--          <div class="stat-item" :class="{ danger: alertCount > 0 }">-->
<!--            <span class="material-symbols-outlined">{{ alertCount > 0 ? 'warning' : 'check_circle' }}</span>-->
<!--            <div class="stat-info">-->
<!--              <span class="stat-value">{{ alertCount }}</span>-->
<!--              <span class="stat-label">Alerte{{ alertCount > 1 ? 's' : '' }}</span>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->

        <!-- Clients -->
        <div class="clients-container">
          <section
              class="client-section"
              v-for="client in filteredClients"
              :key="client.id"
          >
            <!-- Client Header -->
            <div class="client-header">
              <div class="client-title">
                <div class="client-avatar">
                  {{ getInitials(client.name) }}
                </div>
                <div class="client-info">
                  <h2>{{ client.name }}</h2>
                  <span class="client-meta">
                    {{ getClientSites(client.id).length }} univers •
                    {{ getClientSensorCount(client.id) }} capteur{{ getClientSensorCount(client.id) > 1 ? 's' : '' }}
                  </span>
                </div>
              </div>
              <div class="client-status" :class="getClientStatus(client.id)">
                <span class="material-symbols-outlined">
                  {{ getClientStatus(client.id) === 'danger' ? 'error' : 'check_circle' }}
                </span>
                <span>{{ getClientAlertCount(client.id) }} alerte{{ getClientAlertCount(client.id) !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <!-- Plan View (for specific clients) -->
            <div class="plan-section" v-if="client.id === 6 && !isMobile && mode === 'CO2'">
              <div class="plan-header">
                <span class="material-symbols-outlined">map</span>
                <span>Vue du plan</span>
                <button class="plan-toggle" @click="planExpanded = !planExpanded">
                  <span class="material-symbols-outlined">{{ planExpanded ? 'expand_less' : 'expand_more' }}</span>
                </button>
              </div>
              <transition name="expand">
                <div class="plan-content" v-if="planExpanded">
                  <ev-plan
                      :arrayed-sensor="getClientSites(client.id)"
                      :CO2Sites="CO2Sites"
                      :movingSize="0"
                  ></ev-plan>
                </div>
              </transition>
            </div>

            <!-- Sites -->
            <div class="sites-container">
              <div
                  class="site-card"
                  v-for="site in getClientSites(client.id)"
                  :key="site.id"
                  :class="{ 'has-alerts': siteHasAlert(site.id) }"
              >
                <!-- Site Header -->
                <div class="site-header">
                  <div class="site-info">
                    <div class="site-icon" :class="getSiteStatus(site.id)">
                      <span class="material-symbols-outlined">
                        {{ mode === 'CO2' ? 'air' : 'electric_bolt' }}
                      </span>
                    </div>
                    <div class="site-details">
                      <h3>{{ site.name }}</h3>
                      <span class="site-meta">
                        {{ getSiteSensorCount(site.id) }} mesure{{ getSiteSensorCount(site.id) > 1 ? 's' : '' }}
                      </span>
                    </div>
                  </div>
                  <div class="site-status-badge" :class="getSiteStatus(site.id)">
                    <span class="material-symbols-outlined">
                      {{ getSiteStatusIcon(site.id) }}
                    </span>
                  </div>
                </div>

                <!-- Sensors List -->
                <div class="sensors-list">
                  <div
                      class="sensor-item"
                      v-for="(sensor, idx) in getSiteSensors(site.id)"
                      :key="idx"
                      :class="getSensorStatus(sensor)"
                  >
                    <!-- Disabled Sensor -->
                    <div class="sensor-disabled" v-if="sensor.disabled">
                      <span class="material-symbols-outlined">sensors_off</span>
                      <span>Capteur inactif</span>
                    </div>

                    <!-- Active Sensor -->
                    <template v-else>
                      <!-- Sensor Main Info -->
                      <div class="sensor-main">
                        <div class="sensor-header">
                          <span class="sensor-name">{{ sensor.name }}</span>
                          <span class="sensor-status-icon" :class="getSensorStatus(sensor)">
                            <span class="material-symbols-outlined">
                              {{ sensor.value > sensor.max ? 'warning' : 'check_circle' }}
                            </span>
                          </span>
                        </div>

                        <!-- Value Display -->
                        <div class="sensor-value-row">
                          <div class="current-value">
                            <span class="value-number">{{ formatValue(sensor.value) }}</span>
                            <span class="value-unit">{{ mode === 'CO2' ? 'ppm' : 'kWh' }}</span>
                          </div>
                          <div class="threshold-info" v-if="sensor.max < 9999999">
                            <span class="threshold-label">Seuil</span>
                            <span class="threshold-value">{{ sensor.max }}</span>
                          </div>
                        </div>

                        <!-- Progress Bar -->
                        <div class="sensor-progress" v-if="sensor.max < 9999999">
                          <div class="progress-track">
                            <div
                                class="progress-fill"
                                :class="{ exceeded: sensor.value > sensor.max }"
                                :style="{ width: getProgressWidth(sensor) + '%' }"
                            ></div>
                          </div>
                          <div class="progress-labels">
                            <span>0</span>
                            <span class="threshold-marker">{{ sensor.max }}</span>
                          </div>
                        </div>

                        <!-- Last Update -->
                        <div class="sensor-timestamp">
                          <span class="material-symbols-outlined">schedule</span>
                          <span>{{ sensor.date }}</span>
                        </div>
                      </div>

                      <!-- Alert Section (Last Exceeded) - Only for CO2 -->
                      <div class="sensor-alert-section" v-if="mode === 'CO2'">
                        <!-- Has exceeded before -->
                        <div class="last-exceeded" v-if="sensor.hasMaxValue">
                          <div class="exceeded-header">
                            <span class="material-symbols-outlined">notification_important</span>
                            <span>Dernier dépassement</span>
                          </div>
                          <div class="exceeded-data">
                            <div class="exceeded-value">
                              <span class="exceeded-number">{{ formatValue(sensor.valueMax) }}</span>
                              <span class="exceeded-unit">ppm</span>
                            </div>
                            <div class="exceeded-time">
                              <span class="material-symbols-outlined">event</span>
                              <span>{{ sensor.dateMax }}</span>
                            </div>
                          </div>
                          <div class="exceeded-diff" v-if="sensor.valueMax && sensor.max">
                            +{{ formatValue(sensor.valueMax - sensor.max) }} au-dessus du seuil
                          </div>
                        </div>

                        <!-- Never exceeded -->
                        <div class="no-exceeded" v-else>
                          <span class="material-symbols-outlined">verified</span>
                          <span>Aucun dépassement enregistré</span>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Empty State -->
                  <div class="sensors-empty" v-if="!CO2Sites[site.id] || CO2Sites[site.id].length === 0">
                    <span class="material-symbols-outlined">sensors_off</span>
                    <span>Aucun capteur configuré</span>
                  </div>
                </div>

                <!-- Site Footer -->
                <div class="site-footer" @click="goToDashboard(site.id)">
                  <span>Voir plus de détails</span>
                  <span class="material-symbols-outlined">arrow_forward</span>
                </div>
              </div>
            </div>

            <!-- No Sites -->
            <div class="no-sites" v-if="getClientSites(client.id).length === 0">
              <span class="material-symbols-outlined">domain_disabled</span>
              <h4>Aucun site configuré</h4>
              <p>Ce client n'a pas de sites avec la surveillance {{ mode === 'CO2' ? 'CO2' : 'électrique' }} activée.</p>
            </div>
          </section>
        </div>

        <!-- No Clients -->
        <div class="no-clients" v-if="filteredClients.length === 0">
          <div class="empty-illustration">
            <span class="material-symbols-outlined">folder_off</span>
          </div>
          <h3>Aucune donnée disponible</h3>
          <p>Aucun client n'a de surveillance {{ mode === 'CO2' ? 'CO2' : 'électrique' }} configurée.</p>
        </div>
      </div>
    </main>

    <!-- Floating Alert Indicator -->
    <transition name="bounce">
      <div class="floating-alert" v-if="isFullyWarn && alertDismissed" @click="scrollToFirstAlert">
        <span class="material-symbols-outlined">warning</span>
        <span class="alert-count">{{ alertCount }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { buildSortSizeQuery, buildSortSizeMoreQuery } from "../elastic/queries";
import evPlan from "./evPlan.vue";

export default {
  name: 'SecurityDashboard',

  components: {
    evPlan
  },

  data() {
    return {
      profile: this.$store.state.profile.user,
      securityReady: false,
      loading: false,
      isFullyWarn: false,
      alertDismissed: false,
      planExpanded: true,
      CO2Sites: {},
      mode: 'CO2',
      showWelcomePopup: true
    };
  },

  computed: {
    isMobile() {
      return this.$isMobile ? this.$isMobile() : window.innerWidth < 768;
    },

    filteredClients() {
      return this.profile.clients.filter(client => {
        return this.getClientSites(client.id).length > 0;
      });
    },

    globalStatus() {
      if (!this.securityReady) return 'loading';
      if (this.isFullyWarn) return 'danger';
      return 'ok';
    },

    statusText() {
      if (!this.securityReady) return 'Chargement en cours...';
      if (this.isFullyWarn) return `${this.alertCount} alerte${this.alertCount > 1 ? 's' : ''} active${this.alertCount > 1 ? 's' : ''}`;
      return 'Tous les systèmes sont normaux';
    },

    totalSites() {
      const key = this.mode === 'CO2' ? 'is_securityCO2' : 'is_securityElec';
      return this.profile.sites.filter(s => s[key] === 1).length;
    },

    totalSensors() {
      let count = 0;
      Object.values(this.CO2Sites).forEach(sensors => {
        count += sensors.filter(s => !s.disabled).length;
      });
      return count;
    },

    alertCount() {
      let count = 0;
      Object.values(this.CO2Sites).forEach(sensors => {
        sensors.forEach(s => {
          if (!s.disabled && s.value > s.max) count++;
        });
      });
      return count;
    }
  },

  watch: {
    mode() {
      this.CO2Sites = {};
      this.securityReady = false;
      this.isFullyWarn = false;
      this.alertDismissed = false;
      this.updateUserProfile();
    }
  },

  mounted() {
    this.updateUserProfile();
    setInterval(this.refresh, 120000);
  },

  methods: {
    
    closeWelcomePopup() {
      this.showWelcomePopup = false;
    },

    switchMode(newMode) {
      if (this.mode !== newMode) {
        this.mode = newMode;
      }
    },

    refresh() {
      this.loading = true;
      this.CO2Sites = {};
      this.isFullyWarn = false;
      this.updateUserProfile();
    },

    roundDecimal(nombre, precision = 2) {
      const tmp = Math.pow(10, precision);
      return Math.round(nombre * tmp) / tmp;
    },

    formatValue(val) {
      if (val === null || val === undefined) return '—';
      const num = parseFloat(val);

      if (isNaN(num)) return '—';
      if (num >= 1000) {
        return num.toLocaleString('fr-FR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }
      else if (num >= 100) {
        return num.toLocaleString('fr-FR', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        });
      } else {
        return num.toLocaleString('fr-FR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    },

    getInitials(name) {
      if (!name) return '?';
      const words = name.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    goToDashboard(siteId) {
      this.$router.push({
        name: 'Dashboard',
        params: { site_id: siteId }
      });
    },

    scrollToFirstAlert() {
      const alertElement = document.querySelector('.sensor-item.danger');
      if (alertElement) {
        alertElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    getClientSites(clientId) {
      const key = this.mode === 'CO2' ? 'is_securityCO2' : 'is_securityElec';
      return this.profile.sites.filter(s => s.client_id === clientId && s[key] === 1);
    },

    getClientSensorCount(clientId) {
      let count = 0;
      this.getClientSites(clientId).forEach(site => {
        const sensors = this.CO2Sites[site.id] || [];
        count += sensors.filter(s => !s.disabled).length;
      });
      return count;
    },

    getClientAlertCount(clientId) {
      let count = 0;
      this.getClientSites(clientId).forEach(site => {
        const sensors = this.CO2Sites[site.id] || [];
        sensors.forEach(s => {
          if (!s.disabled && s.value > s.max) count++;
        });
      });
      return count;
    },

    getClientStatus(clientId) {
      return this.getClientAlertCount(clientId) > 0 ? 'danger' : 'ok';
    },

    getSiteSensors(siteId) {
      const sensors = this.CO2Sites[siteId] || [];
      return [...sensors].sort((a, b) => {
        if (a.disabled && !b.disabled) return 1;
        if (!a.disabled && b.disabled) return -1;
        if (a.name && b.name) return a.name.localeCompare(b.name);
        return 0;
      });
    },

    getSiteSensorCount(siteId) {
      const sensors = this.CO2Sites[siteId] || [];
      return sensors.filter(s => !s.disabled).length;
    },

    siteHasAlert(siteId) {
      const sensors = this.CO2Sites[siteId] || [];
      return sensors.some(s => !s.disabled && s.value > s.max);
    },

    getSiteStatus(siteId) {
      const sensors = this.CO2Sites[siteId] || [];
      if (sensors.length === 0) return 'empty';
      if (sensors.every(s => s.disabled)) return 'disabled';
      if (sensors.some(s => !s.disabled && s.value > s.max)) return 'danger';
      return 'ok';
    },

    getSiteStatusIcon(siteId) {
      const status = this.getSiteStatus(siteId);
      const icons = {
        empty: 'sensors_off',
        disabled: 'sensors_off',
        danger: 'error',
        ok: 'check_circle'
      };
      return icons[status] || 'help';
    },

    getSensorStatus(sensor) {
      if (sensor.disabled) return 'disabled';
      if (sensor.value > sensor.max) return 'danger';
      return 'ok';
    },

    getProgressWidth(sensor) {
      if (!sensor.max || sensor.max === 0 || sensor.max >= 9999999) return 0;
      const percent = (sensor.value / sensor.max) * 100;
      return Math.min(Math.max(percent, 0), 120); // Allow overflow up to 120%
    },

    updateUserProfile() {
      this.loading = true;

      this.$store.dispatch('profileRequest').then(() => {
        this.profile = this.$store.state.profile.user;

        const key = this.mode === 'CO2' ? 'securityCO2_mesure' : 'securityElec_mesure';
        const condition = this.mode === 'CO2' ? 'is_securityCO2' : 'is_securityElec';

        const filteredSites = this.profile.sites.filter(s => s[condition] === 1);

        let pendingRequests = 0;

        filteredSites.forEach(site => {
          if (site[key] !== null && site[key] !== "null") {
            try {
              const data = JSON.parse(site[key]);
              data.forEach(sensorMesure => {
                pendingRequests++;
                const max = !isNaN(sensorMesure.max) ? sensorMesure.max : 9999999999999;
                this.searchForLastValue(
                    site.id,
                    site.client_id,
                    sensorMesure.csv_column,
                    max,
                    sensorMesure.display_name,
                    { x: sensorMesure.X, y: sensorMesure.Y },
                    () => {
                      pendingRequests--;
                      if (pendingRequests === 0) {
                        this.loading = false;
                      }
                    }
                );
              });
            } catch (e) {
              console.error('Error parsing sensor config:', e);
              this.$set(this.CO2Sites, site.id, [{ disabled: true }]);
            }
          } else {
            this.$set(this.CO2Sites, site.id, [{ disabled: true }]);
          }
        });

        this.securityReady = true;

        if (pendingRequests === 0) {
          this.loading = false;
        }
      }).catch(err => {
        console.error('Error fetching profile:', err);
        this.loading = false;
        this.securityReady = true;
      });
    },

    searchForLastValue(site_id, client_id, mesure, max, display_name, position, callback) {
      const elastic_winery_mesures_index = "winery_mesures_" + client_id;
      const elasticQuery = buildSortSizeQuery(site_id, null, "desc", mesure);

      this.$store
          .dispatch("elasticSearchRequest", {
            index: elastic_winery_mesures_index,
            body: elasticQuery,
            size: 1,
            site: site_id,
          })
          .then(result => {
            if (result.data.hits.hits.length > 0) {
              const currentSite = this.profile.sites.find(s => s.id === site_id);
              const hit = result.data.hits.hits[0];

              let dateRes = hit.sort[0];
              if (dateRes.toString().length === 13) {
                dateRes = (Math.floor(dateRes / 1000) - 3600) * 1000;
              }

              const actualDate = new Date(dateRes);
              if (currentSite && currentSite.time_offset) {
                actualDate.setHours(actualDate.getHours() + currentSite.time_offset);
              }

              const newSensor = {
                disabled: false,
                hasMaxValue: false,
                valueMax: 0,
                dateMax: "",
                value: this.roundDecimal(parseFloat(hit._source[mesure]), 3),
                date: actualDate.toLocaleDateString('fr-fr', {
                  year: "2-digit",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit"
                }),
                max: parseFloat(max),
                name: display_name,
                position: position
              };

              if (newSensor.value > newSensor.max && !this.isFullyWarn) {
                this.isFullyWarn = true;
              }

              // Search for last exceeded value
              const elasticQueryMax = buildSortSizeMoreQuery(site_id, null, "desc", mesure, max);

              this.$store
                  .dispatch("elasticSearchRequest", {
                    index: elastic_winery_mesures_index,
                    body: elasticQueryMax,
                    size: 1,
                    site: site_id,
                  })
                  .then(resultMax => {
                    if (resultMax.data.hits.hits.length > 0) {
                      const hitMax = resultMax.data.hits.hits[0];

                      let dateResMax = hitMax.sort[0];
                      if (dateResMax.toString().length === 13) {
                        dateResMax = (Math.floor(dateResMax / 1000) - 3600) * 1000;
                      }

                      const actualMaxDate = new Date(dateResMax);
                      if (currentSite && currentSite.time_offset) {
                        actualMaxDate.setHours(actualMaxDate.getHours() + currentSite.time_offset);
                      }

                      newSensor.valueMax = this.roundDecimal(parseFloat(hitMax._source[mesure]), 3);
                      newSensor.dateMax = actualMaxDate.toLocaleDateString('fr-fr', {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit"
                      });

                      if (newSensor.valueMax >= newSensor.max) {
                        newSensor.hasMaxValue = true;
                      }
                    }

                    this.addSensorData(site_id, newSensor);
                    if (callback) callback();
                  })
                  .catch(err => {
                    console.error('Error fetching max value:', err);
                    this.addSensorData(site_id, newSensor);
                    if (callback) callback();
                  });
            } else {
              this.addSensorData(site_id, { disabled: true, name: display_name });
              if (callback) callback();
            }
          })
          .catch(err => {
            console.error('Error fetching sensor data:', err);
            this.addSensorData(site_id, { disabled: true, name: display_name });
            if (callback) callback();
          });
    },

    addSensorData(site_id, data) {
      if (!this.CO2Sites[site_id]) {
        this.$set(this.CO2Sites, site_id, [data]);
      } else {
        const existing = this.CO2Sites[site_id];
        this.$set(this.CO2Sites, site_id, [...existing, data]);
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.security-dashboard {
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-dark: #1e3d29;
  --color-primary-bg: #e8f0eb;

  --color-accent: #5a9e6f;
  --color-accent-light: #a8d4b8;

  --color-surface: #ffffff;
  --color-surface-secondary: #f8faf9;
  --color-surface-tertiary: #f0f4f2;
  --color-surface-hover: #eef2ef;
  --color-background: #f3f6f4;

  --color-border: #dce5df;
  --color-border-light: #e8eeea;

  --color-text-primary: #1a2e1f;
  --color-text-secondary: #4a5d4f;
  --color-text-muted: #7a8d7f;
  --color-text-light: #a0b0a5;

  --color-danger: #dc2626;
  --color-danger-light: #fca5a5;
  --color-danger-bg: #fef2f2;
  --color-danger-border: #fecaca;

  --color-warning: #d97706;
  --color-warning-bg: #fffbeb;

  --color-success: #059669;
  --color-success-light: #6ee7b7;
  --color-success-bg: #ecfdf5;

  --color-info: #0284c7;
  --color-info-bg: #e0f2fe;

  --color-elec: #7c3aed;
  --color-elec-bg: #f5f3ff;

  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);

  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-full: 9999px;

  --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-normal: 200ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: 300ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--color-background);
  color: var(--color-text-primary);
  overflow: hidden;
  max-height: calc(100% - 10px);
}

/* ============================================
   HEADER
   ============================================ */
.dashboard-header {
  flex-shrink: 0;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  /*z-index: 100;*/
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  max-width: 1800px;
  margin: 0 auto;
  gap: 16px;
}

@media (max-width: 640px) {
  .header-content {
    padding: 12px 16px;
    flex-wrap: wrap;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  width: 46px;
  height: 46px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-warning-bg) 0%, #fef3c7 100%);
  flex-shrink: 0;
}

.header-icon.co2 {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.header-icon.co2 .material-symbols-outlined {
  color: var(--color-warning);
}

.header-icon.elec {
  background: linear-gradient(135deg, var(--color-elec-bg) 0%, #ede9fe 100%);
}

.header-icon.elec .material-symbols-outlined {
  color: var(--color-elec);
}

.header-icon .material-symbols-outlined {
  font-size: 24px;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.01em;
}

@media (max-width: 640px) {
  .header-title {
    font-size: 1rem;
  }
}

.header-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-text-muted);
  flex-shrink: 0;
}

.status-dot.ok {
  background: var(--color-success);
  box-shadow: 0 0 0 3px var(--color-success-bg);
}

.status-dot.danger {
  background: var(--color-danger);
  box-shadow: 0 0 0 3px var(--color-danger-bg);
  animation: pulse 1.5s ease-in-out infinite;
}

.status-dot.loading {
  background: var(--color-text-muted);
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Mode Toggle */
.mode-toggle {
  display: flex;
  padding: 4px;
  background: var(--color-surface-tertiary);
  border-radius: var(--radius-md);
  gap: 4px;
}

.mode-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.mode-btn:hover {
  color: var(--color-text-primary);
  background: var(--color-surface-hover);
}

.mode-btn.active {
  background: var(--color-surface);
  color: var(--color-warning);
  box-shadow: var(--shadow-sm);
}

.mode-btn.elec.active {
  color: var(--color-elec);
}

.mode-btn .material-symbols-outlined {
  font-size: 18px;
}

.mode-label {
  display: none;
}

@media (min-width: 640px) {
  .mode-label {
    display: inline;
  }
}

/* Refresh Button */
.btn-refresh {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
}

.btn-refresh:hover:not(:disabled) {
  background: var(--color-surface-secondary);
  color: var(--color-text-primary);
  border-color: var(--color-primary-lighter);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-refresh .material-symbols-outlined {
  font-size: 20px;
}

.btn-refresh .spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   ALERT BAR
   ============================================ */
.alert-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: linear-gradient(90deg, var(--color-danger) 0%, #ef4444 100%);
  color: white;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.alert-icon {
  font-size: 22px;
  animation: pulse 1s ease-in-out infinite;
}

.alert-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.alert-text strong {
  font-size: 0.875rem;
  font-weight: 600;
}

.alert-text span {
  font-size: 0.8125rem;
  opacity: 0.9;
}

.alert-dismiss {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
}

.alert-dismiss:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* ============================================
   MAIN CONTENT (SCROLLABLE)
   ============================================ */
.dashboard-main {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.dashboard-content {
  max-width: 1800px;
  margin: 0 auto;
  padding: 24px;
}

@media (max-width: 640px) {
  .dashboard-content {
    padding: 16px;
  }
}

/* ============================================
   LOADING OVERLAY
   ============================================ */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 48px;
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.loading-card p {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* ============================================
   STATS BAR
   ============================================ */
.stats-bar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}

@media (max-width: 800px) {
  .stats-bar {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .stats-bar {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  transition: var(--transition-normal);
}

.stat-item:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--color-border);
}

.stat-item .material-symbols-outlined {
  font-size: 26px;
  color: var(--color-primary-lighter);
}

.stat-item.danger .material-symbols-outlined {
  color: var(--color-danger);
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
}

.stat-item.danger .stat-value {
  color: var(--color-danger);
}

.stat-label {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ============================================
   CLIENT SECTION
   ============================================ */
.clients-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.client-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.client-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: var(--color-surface-secondary);
  border-bottom: 1px solid var(--color-border-light);
  gap: 16px;
  flex-wrap: wrap;
}

.client-title {
  display: flex;
  align-items: center;
  gap: 14px;
}

.client-avatar {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
}

.client-info h2 {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.client-meta {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.client-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: var(--radius-full);
  font-size: 0.8125rem;
  font-weight: 500;
}

.client-status.ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.client-status.danger {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.client-status .material-symbols-outlined {
  font-size: 16px;
}

/* ============================================
   PLAN SECTION
   ============================================ */
.plan-section {
  border-bottom: 1px solid var(--color-border-light);
}

.plan-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  background: var(--color-surface-tertiary);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
}

.plan-header .material-symbols-outlined {
  font-size: 18px;
  color: var(--color-accent);
}

.plan-toggle {
  margin-left: auto;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.plan-content {
  padding: 20px 24px;
  background: var(--color-surface);
}

/* ============================================
   SITES CONTAINER
   ============================================ */
/*.sites-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 24px;
}

.sites-container > *:only-child {
  grid-column: 1 / -1;
}

.sites-container > *:only-child > .sensors-list {
  grid-template-columns: repeat(4, 1fr);
}*/

.sites-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 24px;
}

@container (min-width: 1000px){
  .sensors-list {
    grid-template-columns: repeat(4, 1fr) !important;
  }
}

@media (max-width: 768px){
  .site-card {

    min-width: 100%! important;
  }
}

@media (max-width: 1280px){
  @container (min-width: 550px and max-width: 1000px){
    .sensors-list {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }
  @container (max-width: 550px){
    .sensors-list {
      grid-template-columns: repeat(1, 1fr) !important;
    }
  }
}

@media (max-width: 480px) {
  .sites-container {
    padding: 16px;
    gap: 16px;
  }
  .sensors-list {
    grid-template-columns: repeat(1, 1fr) !important;
  }
}

/* ============================================
   SITE CARD
   ============================================ */
.site-card {
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: var(--transition-normal);
  flex: 1 1 0;
  min-width: calc(50% - 10px);  /* Won't shrink below 50% */
  max-width: 100%;             /* Can grow to 100% if alone */
  container-type: inline-size;
}

.site-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--color-border);
}

.site-card.has-alerts {
  border-color: var(--color-danger-border);
  box-shadow: 0 0 0 1px var(--color-danger-light);
}

.site-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border-light);
  /*cursor: pointer;*/
  transition: var(--transition-fast);
}

/*.site-header:hover {
  background: var(--color-surface-hover);
}*/

.site-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.site-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.site-icon.ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.site-icon.danger {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.site-icon.disabled,
.site-icon.empty {
  background: var(--color-surface-tertiary);
  color: var(--color-text-muted);
}

.site-icon .material-symbols-outlined {
  font-size: 20px;
}

.site-details h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.site-meta {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.site-status-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.site-status-badge.ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.site-status-badge.danger {
  background: var(--color-danger-bg);
  color: var(--color-danger);
  animation: pulse 2s ease-in-out infinite;
}

.site-status-badge.disabled,
.site-status-badge.empty {
  background: var(--color-surface-tertiary);
  color: var(--color-text-muted);
}

.site-status-badge .material-symbols-outlined {
  font-size: 18px;
}

/* ============================================
   SENSORS LIST
   ============================================ */
.sensors-list {
  padding: 16px;
  /*display: flex;
  flex-direction: column;*/
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

/* ============================================
   SENSOR ITEM
   ============================================ */
.sensor-item {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  /*border-radius: var(--radius-md);*/
  overflow: hidden;
  transition: var(--transition-normal);
}

.sensor-item:hover {
  border-color: var(--color-border);
}

.sensor-item.ok {
  border-left: 4px solid var(--color-success);
}

.sensor-item.danger {
  border-left: 4px solid var(--color-danger);
  background: var(--color-danger-bg);
}

.sensor-item.disabled {
  border-left: 4px solid var(--color-text-light);
  opacity: 0.7;
}

.sensor-disabled {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px 16px;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.sensor-disabled .material-symbols-outlined {
  font-size: 24px;
  opacity: 0.5;
}

/* Sensor Main */
.sensor-main {
  padding: 16px;
}

.sensor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sensor-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.sensor-status-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sensor-status-icon.ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.sensor-status-icon.danger {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.sensor-status-icon .material-symbols-outlined {
  font-size: 16px;
}

/* Value Row */
.sensor-value-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 14px;
}

.current-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.value-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text-primary);
  font-family: 'SF Mono', 'Roboto Mono', monospace;
  letter-spacing: -0.02em;
  white-space: pre;
}

.sensor-item.danger .value-number {
  color: var(--color-danger);
}

.value-unit {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

.threshold-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.threshold-label {
  font-size: 0.6875rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.threshold-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  font-family: 'SF Mono', 'Roboto Mono', monospace;
}

/* Progress Bar */
.sensor-progress {
  margin-bottom: 14px;
}

.progress-track {
  height: 6px;
  background: var(--color-surface-tertiary);
  border-radius: 3px;
  overflow: visible;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-success) 0%, var(--color-success-light) 100%);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-fill.exceeded {
  background: linear-gradient(90deg, var(--color-danger) 0%, var(--color-danger-light) 100%);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 0.6875rem;
  color: var(--color-text-muted);
}

.threshold-marker {
  color: var(--color-text-secondary);
  font-weight: 500;
}

/* Timestamp */
.sensor-timestamp {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.sensor-timestamp .material-symbols-outlined {
  font-size: 14px;
}

/* Alert Section */
.sensor-alert-section {
  border-top: 1px solid var(--color-border-light);
}

.last-exceeded {
  padding: 14px 16px;
  background: linear-gradient(135deg, var(--color-danger-bg) 0%, #fff1f2 100%);
}

.exceeded-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-danger);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.exceeded-header .material-symbols-outlined {
  font-size: 14px;
}

.exceeded-data {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.exceeded-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.exceeded-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-danger);
  font-family: 'SF Mono', 'Roboto Mono', monospace;
}

.exceeded-unit {
  font-size: 0.75rem;
  color: var(--color-danger);
  opacity: 0.8;
}

.exceeded-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.exceeded-time .material-symbols-outlined {
  font-size: 14px;
}

.exceeded-diff {
  font-size: 0.75rem;
  color: var(--color-danger);
  font-weight: 500;
}

.no-exceeded {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: var(--color-success-bg);
  font-size: 0.8125rem;
  color: var(--color-success);
}

.no-exceeded .material-symbols-outlined {
  font-size: 16px;
}

/* ============================================
   SITE FOOTER
   ============================================ */
.site-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 18px;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border-light);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-primary);
  cursor: pointer;
  transition: var(--transition-fast);
}

.site-footer:hover {
  background: var(--color-primary-bg);
}

.site-footer .material-symbols-outlined {
  font-size: 16px;
  transition: transform var(--transition-fast);
}

.site-footer:hover .material-symbols-outlined {
  transform: translateX(4px);
}

/* ============================================
   EMPTY STATES
   ============================================ */
.sensors-empty,
.no-sites,
.no-clients {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  color: var(--color-text-muted);
}

.sensors-empty {
  padding: 32px 16px;
}

.sensors-empty .material-symbols-outlined {
  font-size: 32px;
  opacity: 0.4;
  margin-bottom: 8px;
}

.no-sites {
  grid-column: 1 / -1;
}

.no-sites .material-symbols-outlined,
.no-clients .material-symbols-outlined {
  font-size: 48px;
  opacity: 0.4;
  margin-bottom: 16px;
}

.no-sites h4,
.no-clients h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 0 0 8px;
}

.no-sites p,
.no-clients p {
  font-size: 0.875rem;
  margin: 0;
  max-width: 300px;
}

.no-clients {
  min-height: 400px;
}

.empty-illustration .material-symbols-outlined {
  font-size: 64px;
}

/* ============================================
   FLOATING ALERT
   ============================================ */
.floating-alert {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: var(--color-danger);
  color: white;
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  z-index: 100;
  transition: var(--transition-normal);
}

.floating-alert:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-xl);
}

.floating-alert .material-symbols-outlined {
  font-size: 20px;
}

.alert-count {
  font-size: 0.9375rem;
  font-weight: 600;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.bounce-enter-active {
  animation: bounce-in 0.4s ease-out;
}

.bounce-leave-active {
  animation: bounce-in 0.3s ease-in reverse;
}

@keyframes bounce-in {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

/* ============================================
   WELCOME POPUP
   ============================================ */
.welcome-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.welcome-popup {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  max-width: 440px;
  width: 100%;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
  animation: popup-enter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.welcome-popup.has-alerts {
  border: 2px solid var(--color-danger-border);
}

@keyframes popup-enter {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-success-bg) 0%, #d1fae5 100%);
  flex-shrink: 0;
}

.popup-icon .material-symbols-outlined {
  font-size: 48px;
  color: var(--color-success);
}

.popup-icon.alert {
  background: linear-gradient(135deg, var(--color-danger-bg) 0%, #fee2e2 100%);
  animation: pulse-icon 2s ease-in-out infinite;
}

.popup-icon.alert .material-symbols-outlined {
  color: var(--color-danger);
}

@keyframes pulse-icon {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.popup-content h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.01em;
}

.popup-content p {
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.btn-confirm {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-full);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
  box-shadow: 0 4px 12px rgba(45, 90, 61, 0.2);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(45, 90, 61, 0.3);
}

.btn-confirm:active {
  transform: translateY(0);
}

.btn-confirm .material-symbols-outlined {
  font-size: 20px;
}

.welcome-popup.has-alerts .btn-confirm {
  background: linear-gradient(135deg, var(--color-danger) 0%, #ef4444 100%);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.welcome-popup.has-alerts .btn-confirm:hover {
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.3);
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .welcome-popup {
  animation: popup-enter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Mobile */
@media (max-width: 480px) {
  .welcome-popup {
    padding: 32px 24px;
  }

  .popup-icon {
    width: 64px;
    height: 64px;
  }

  .popup-icon .material-symbols-outlined {
    font-size: 36px;
  }

  .popup-content h2 {
    font-size: 1.25rem;
  }

  .popup-content p {
    font-size: 0.9375rem;
  }
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>